import React, { useMemo } from "react";
import clsx from "clsx";
import VerticalCollapse from "../VerticalCollapse";
import VerticalItem from "../VerticalItem";
import IntlMessages from "../../../../../utility/IntlMessages";
import { checkPermission } from "../../../../../utility/helper/RouteHelper";
import { useAuthUser } from "../../../../../utility/AuthHooks";
import { useSidebarContext } from "../../../../../utility/AppContextProvider/SidebarContextProvider";
import VerticalNavGroupItem from "./VerticalNavGroupItem";
import { RouterConfigData } from "../../../../../../pages/routesConfig";

interface VerticalNavGroupProps {
  item?: RouterConfigData;
  level?: number;
  activeCollapseIds: { [level: number]: string | null };
  handleToggleCollapse: (level: number, id: string) => void;
}

const VerticalNavGroup: React.FC<VerticalNavGroupProps> = ({
  item,
  level = 0,
  activeCollapseIds,
  handleToggleCollapse,
}) => {
  const { sidebarTextColor } = useSidebarContext();
  const { user } = useAuthUser();
  const hasPermission = useMemo(
    () => checkPermission(item!.permittedRole, user.role),
    [item, user.role]
  );

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      <VerticalNavGroupItem
        level={level}
        sidebarTextColor={sidebarTextColor}
        component="div"
        className={clsx("nav-item nav-item-header")}
      >
        <IntlMessages id={item!.messageId} />
      </VerticalNavGroupItem>

      {item!.children && (
        <>
          {item!.children.map((child) => (
            <React.Fragment key={child.id}>
              {child.type === "group" && (
                <VerticalNavGroup
                  item={child}
                  level={level + 1}
                  activeCollapseIds={activeCollapseIds}
                  handleToggleCollapse={handleToggleCollapse}
                />
              )}

              {child.type === "collapse" && (
                <VerticalCollapse
                  item={child}
                  level={level + 1}
                  activeCollapseIds={activeCollapseIds}
                  handleToggleCollapse={handleToggleCollapse}
                />
              )}

              {child.type === "item" && (
                <VerticalItem item={child} level={level + 1} />
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  );
};

export default VerticalNavGroup;
